import React, {
  useCallback,
  useEffect,
} from "react";

import { useTranslation } from "react-i18next";

import {
  OnboardingMenu,
  OnboardingOverlay,
  useOnboardingController,
} from "./index.jsx";

import {
  emitAppEvent,
  subscribeAppEvents,
  subscribeOnboardingCommands,
} from "./events.js";

const RATION_STEPS = new Set([
  "overview-ration-intro",
  "overview-photo-intro",
  "overview-breakfast-add",
  "overview-manual-add",
  "overview-manual-form",
  "overview-breakfast-macros",
  "overview-day-summary",
  "overview-day-switch",
]);

const OVERVIEW_STEPS = new Set([
  "overview-macro-edit",
  "overview-rings",
  "overview-calendar",
  "overview-wellness",
  "overview-report",
  "overview-complete",
]);

function getTarget(selector) {
  if (
    !selector ||
    typeof document === "undefined"
  ) {
    return null;
  }

  return document.querySelector(
    selector
  );
}

function tapTarget(
  selector,
  {
    delay = 0,
    retries = 20,
    interval = 90,
  } = {}
) {
  if (
    typeof window === "undefined" ||
    typeof document === "undefined"
  ) {
    return;
  }

  let attempt = 0;

  const run = () => {
    const element =
      getTarget(selector);

    if (!element) {
      attempt += 1;

      if (attempt <= retries) {
        window.setTimeout(
          run,
          interval
        );
      }

      return;
    }

    const rect =
      element.getBoundingClientRect();

    const eventOptions = {
      bubbles: true,
      cancelable: true,
      pointerId: 1,
      pointerType: "mouse",
      isPrimary: true,
      clientX:
        rect.left +
        rect.width / 2,
      clientY:
        rect.top +
        rect.height / 2,
    };

    const makePointerEvent = (
      type
    ) => {
      if (
        typeof window.PointerEvent ===
        "function"
      ) {
        return new window.PointerEvent(
          type,
          eventOptions
        );
      }

      return new window.Event(
        type,
        {
          bubbles: true,
          cancelable: true,
        }
      );
    };

    element.dispatchEvent(
      makePointerEvent(
        "pointerdown"
      )
    );

    element.dispatchEvent(
      makePointerEvent(
        "pointerup"
      )
    );

    if (
      element.isConnected &&
      typeof element.click ===
        "function"
    ) {
      element.click();
    }
  };

  window.setTimeout(
    run,
    delay
  );
}

function emitRepeated(
  type,
  payload,
  delays
) {
  if (
    typeof window === "undefined"
  ) {
    return;
  }

  (
    delays || [0, 180, 420]
  ).forEach((delay) => {
    window.setTimeout(() => {
      emitAppEvent(
        type,
        payload
      );
    }, delay);
  });
}

function blurKeyboard(
  delay = 500
) {
  if (
    typeof window === "undefined"
  ) {
    return;
  }

  window.setTimeout(() => {
    const element =
      document.activeElement;

    if (
      element &&
      typeof element.blur ===
        "function"
    ) {
      element.blur();
    }
  }, delay);
}

export default function AppOnboardingBridge({
  openFoodOverview,
  openRationToday,

  onFoodOnboardingStart,
  onFoodOnboardingComplete,

  beginWorkoutOnboarding,
  endWorkoutOnboarding,

  openWorkoutOverview,
  openWorkoutGroups,
  openWorkoutSession,
  prepareWorkoutDemo,
  openWorkoutLibrary,

  closeTransientScreens,
  onLocked,
}) {
  const { i18n } = useTranslation();

  const ensureMyFoodsOpen =
    useCallback(
      (
        tab = "ingredients"
      ) => {
        const header =
          getTarget(
            '[data-onboarding="my-foods-header"]'
          );

        if (!header) {
          openRationToday?.();

          emitRepeated(
            "my-foods:open",
            {
              tab,
            },
            [120, 340, 680]
          );

          return;
        }

        tapTarget(
          tab === "dishes"
            ? '[data-onboarding="my-foods-dishes-tab"]'
            : '[data-onboarding="my-foods-products-tab"]',
          {
            delay: 20,
          }
        );
      },
      [openRationToday]
    );

const prepareStep =
  useCallback(
    (step) => {
      if (!step) return;

      if (
        RATION_STEPS.has(
          step.id
        )
      ) {
        openRationToday?.();
        return;
      }

      if (
        OVERVIEW_STEPS.has(
          step.id
        )
      ) {
        openFoodOverview?.({
          scrollTop:
            step.id ===
              "overview-macro-edit" ||
            step.id ===
              "overview-rings",
        });

        return;
      }

const openDishBuilder =
  () => {
    const builder =
      getTarget(
        '[data-onboarding="dish-builder"]'
      );

    if (builder) {
      if (
        !getTarget(
          '[data-onboarding="dish-builder-selected"]'
        )
      ) {
        emitRepeated(
          "dish-builder:prefill",
          {
            name:
              "Моё новое блюдо",

            grams: 100,
          },
          [
            80,
            260,
            520,
          ]
        );
      }

      blurKeyboard(500);
      return;
    }

    ensureMyFoodsOpen(
      "dishes"
    );

    tapTarget(
      '[data-onboarding="my-foods-add-button"]',
      {
        delay: 360,
        retries: 30,
        interval: 100,
      }
    );

    emitRepeated(
      "dish-builder:prefill",
      {
        name:
          "Моё новое блюдо",

        grams: 100,
      },
      [
        650,
        950,
        1300,
        1650,
      ]
    );

    blurKeyboard(1100);
  };

      const ensureDishList =
        () => {
          ensureMyFoodsOpen(
            "dishes"
          );
        };

const focusCreatedDish =
  ({
    topOffset = 92,
    alignAboveFooter = false,
    footerGap = 28,
  } = {}) => {
    const createdDishSelector =
      '[data-onboarding-created-dish="1"]';

    if (
      !getTarget(
        createdDishSelector
      )
    ) {
      ensureDishList();
    }

    let retryCount = 0;

    const scrollCreatedDish =
      () => {
        const card =
          getTarget(
            createdDishSelector
          );

        const footer =
          alignAboveFooter
            ? getTarget(
                '[data-onboarding-footer="1"]'
              )
            : null;

        if (
          !card ||
          (
            alignAboveFooter &&
            !footer
          )
        ) {
          retryCount += 1;

          if (retryCount <= 8) {
            window.setTimeout(
              scrollCreatedDish,
              100
            );
          }

          return;
        }

        const scrollRoot =
          card.closest(
            '[data-my-foods-scroll-root="1"]'
          );

        if (!scrollRoot) return;

        const rootRect =
          scrollRoot.getBoundingClientRect();

        const cardRect =
          card.getBoundingClientRect();

        const targetViewportTop =
          alignAboveFooter && footer
            ? footer
                .getBoundingClientRect()
                .top -
              footerGap -
              cardRect.height
            : rootRect.top +
              topOffset;

        const safeTargetViewportTop =
          Math.max(
            rootRect.top + 12,
            targetViewportTop
          );

        const nextTop =
          scrollRoot.scrollTop +
          cardRect.top -
          safeTargetViewportTop;

        scrollRoot.scrollTo({
          top: Math.max(
            0,
            nextTop
          ),
          behavior: "auto",
        });
      };

    window.setTimeout(
      scrollCreatedDish,
      60
    );

    window.setTimeout(
      scrollCreatedDish,
      220
    );
  };

      switch (step.id) {
        case
          "workout-form-tools": {
          openWorkoutOverview?.({
            scrollTop: true,
          });

          break;
        }

        case
          "workout-muscle-select": {
          openWorkoutGroups?.({
            resetSelection: true,
          });

          break;
        }

        case
          "workout-muscle-continue": {
          openWorkoutGroups?.({
            resetSelection: false,
          });

          break;
        }

        case
          "workout-session-overview": {
          openWorkoutSession?.({
            scrollTop: true,
          });

          window.setTimeout(
            () => {
              prepareWorkoutDemo?.();
            },
            120
          );

          break;
        }

        case
          "workout-exercise-controls":

        case
          "workout-exercise-drag":

        case
          "workout-title-open":

        case
          "workout-title-form":

        case
          "workout-save":

        case
          "workout-repeat-overview":

        case
          "workout-cancel-repeat": {
          openWorkoutSession?.();

          break;
        }

        case
          "workout-library-filters":

        case
          "workout-card-open":

        case
          "workout-card-details":

        case
          "workout-repeat": {
          openWorkoutLibrary?.();

          break;
        }

        case
          "workout-activity-calendar": {
          openWorkoutOverview?.({
            scrollTop: false,
          });

          break;
        }

        case
          "workout-complete": {
          break;
        }

        case "dish-entry": {
          openRationToday?.();
          break;
        }

        case
          "dish-library-overview":

        case
          "dish-product-menu": {
          ensureMyFoodsOpen(
            "ingredients"
          );

          break;
        }

        case
          "dish-create-intro": {
          ensureMyFoodsOpen(
            "dishes"
          );

          break;
        }

case
  "dish-builder-overview": {
  openDishBuilder();

  const scrollBuilderOverviewToTop =
    () => {
      const builder =
        getTarget(
          '[data-onboarding="dish-builder"]'
        );

      const scrollRoot =
        builder?.closest(
          '[data-modal-scroll-root="1"]'
        );

      if (!scrollRoot) return;

      scrollRoot.scrollTo({
        top: 0,
        behavior: "auto",
      });
    };

  [
    100,
    280,
    520,
  ].forEach((delay) => {
    window.setTimeout(
      scrollBuilderOverviewToTop,
      delay
    );
  });

  break;
}

case
  "dish-builder-save": {
  openDishBuilder();

  const scrollBuilderToTop =
    () => {
      const builder =
        getTarget(
          '[data-onboarding="dish-builder"]'
        );

      const scrollRoot =
        builder?.closest(
          '[data-modal-scroll-root="1"]'
        );

      if (!scrollRoot) return;

      scrollRoot.scrollTo({
        top: 0,
        behavior: "auto",
      });
    };

  window.setTimeout(
    scrollBuilderToTop,
    80
  );

  window.setTimeout(
    scrollBuilderToTop,
    260
  );

  break;
}



case "dish-composition-open": {
  focusCreatedDish({
    topOffset: 92,
  });

  break;
}

case "dish-composition-add": {
  focusCreatedDish({
    alignAboveFooter: true,
    footerGap: 28,
  });

  break;
}

        case
          "dish-complete": {
          break;
        }

        default:
          break;
      }
    },
    [
      ensureMyFoodsOpen,
      openFoodOverview,
      openRationToday,

      openWorkoutOverview,
      openWorkoutGroups,
      openWorkoutSession,
      prepareWorkoutDemo,
      openWorkoutLibrary,
    ]
  );
  const controller =
    useOnboardingController({
      prepareStep,

beforeStart: (
  scenarioId
) => {
  closeTransientScreens?.();
if (
  scenarioId ===
  "overview"
) {
  onFoodOnboardingStart?.();
}
  if (
    scenarioId ===
    "workout"
  ) {
    beginWorkoutOnboarding?.();
  }

  if (
    scenarioId === "dish"
        ) {
          emitAppEvent(
            "my-foods:close"
          );
        }
      },

      afterClose: (
        scenarioId
      ) => {
        closeTransientScreens?.();
if (
  scenarioId ===
  "workout"
) {
  endWorkoutOnboarding?.();
}
        if (
          scenarioId ===
          "overview"
        ) {
          openFoodOverview?.({
            scrollTop: true,
          });
        }

        if (
          scenarioId === "dish"
        ) {
          emitAppEvent(
            "my-foods:close"
          );
        }
      },

afterComplete: (
  scenarioId,
  completion = {}
) => {
  if (
    scenarioId ===
    "workout"
  ) {
    endWorkoutOnboarding?.();
  }

  if (
    scenarioId === "overview"
  ) {
    onFoodOnboardingComplete?.(
      completion
    );
  }

  if (
    scenarioId === "dish"
  ) {
    /*
    Для отдельного онбординга
    «Мои продукты и блюда»
    напоминание не создаём.
    */
    onFoodOnboardingComplete?.();

    emitAppEvent(
      "my-foods:close"
    );
  }
},

      onLocked,
    });

useEffect(() => {
  return subscribeOnboardingCommands(
    ({ command }) => {
      if (
        command ===
        "start-food"
      ) {
        controller.start(
          "overview"
        );
      }
    }
  );
}, [
  controller.start,
]);

  useEffect(() => {
    return subscribeAppEvents(
      ({ type }) => {
                if (
          type ===
            "dish-builder:cancelled" &&
          (
            controller.currentStep
              ?.id ===
              "dish-builder-overview" ||
            controller.currentStep
              ?.id ===
              "dish-builder-save"
          )
        ) {
          controller.back();

          return;
        }
        if (
          type ===
            "manual-form-opened" &&
          controller.currentStep
            ?.id ===
            "overview-manual-add"
        ) {
          emitAppEvent(
            "manual-product:prefill",
            {
              name:
                i18n.resolvedLanguage === "ru"
                  ? "Сыр"
                  : "Cheese",
              proteins: 24,
              fats: 30,
              carbs: 0,
            }
          );

          return;
        }

        if (
          type ===
            "macros:closed" &&
          controller.currentStep
            ?.id ===
            "overview-macro-form"
        ) {
          controller.goToStep(
            0
          );

          return;
        }

        if (
          type ===
            "manual-product:cancelled" &&
          controller.currentStep
            ?.id ===
            "overview-manual-form"
        ) {
          controller.back();
        }
      }
    );
  }, [
    controller.currentStep?.id,
    controller.goToStep,
    controller.back,
  ]);

  return (
    <>
      <OnboardingMenu
        open={
          controller.menuOpen
        }
        progress={
          controller.progress
        }
        onClose={
          controller.close
        }
        onStart={
          controller.start
        }
      />

<OnboardingOverlay
  session={
    controller.session
  }
  onNext={
    controller.next
  }
  onFinish={
    controller.close
  }
/>
    </>
  );
}